import type React from 'react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { withProfiler } from '@sentry/react';

//Redux

import {
  selectHistory,
  selectCurrentPage,
  selectIsMobile,
  selectIsWidgetVisible,
  selectIsHelpButtonVisible,
} from 'reducers/main';
import {
  selectIsPositionLeft,
  selectLaunchStyle,
  selectPlacement,
  selectPositionY,
  selectTabName,
  selectThemeColor,
} from 'reducers/config';
import { sendTrackingEvent } from 'actions/workflows/workflows';
import {
  selectIsBuilderPreview,
  selectWorkflowsConfig,
  selectIsProactiveChatVisible,
  selectIntentTitle,
  selectIsModelTraining,
  selectIsWidgetEnabled,
  selectInitialIntentId,
  selectInitialQuery,
  selectFeatureFlags,
} from 'reducers/workflows';

//Components
import Modal from 'components/Modal';
import HelpButton from 'components/HelpButton';
import ProactiveBubbles from 'components/ProactiveBubbles';
import ModelTrainingIndicator from '../ModelTrainingIndicator/ModelTrainingIndicator';

//Hooks
import {
  useSetupSentry,
  useLogRocket,
  useInitSolve,
  useSetCustomConfigs,
  useInitSolveFromURL,
  useSetTabIdSentryTag,
  useHandleWorkflowTriggerOnClick,
  useHandleOnWidgetLoad,
  useEmitConversationIdGeneratedEvent,
} from './hooks';

//App Routes
import { pageDict } from './Routes';

// Utils
import { usePostMessageListener } from './messageListeners';
import { postMessage } from '../../utils/postMessage';
import useInitApiService from './hooks/useInitApiService';
import { mobileWidthCondition, smallHeightCondition } from '../../embed/utils';
import { sendResizeiframeMessage } from 'components/App/utils';

// Styles
import {
  Container,
  ModalContainer,
  AppWrapper,
  HelpButtonContainer,
} from './styles';
import { useSetLastActiveTabId } from 'persistence/persistenceBetweenTabs';
import { useClearConversationOnTimeout } from 'persistence/useClearConversationOnTimeout';
import useEscapeKeyHandler from './hooks/useEscapeKeyHandler';
import useAnnounceMessageReceived from './hooks/useAnnounceMessageReceived';
import { Unstable_TrapFocus } from '@mui/material';
import useHandleAttemptedToFetchRedactedConversationError from './hooks/useHandleAttemptedToFetchRedactedConversationError';
import { useHandleZendeskChatInitialization } from './hooks/useHandleZendeskChatInitialization';
import { usePusherLiveChatMessageEventSubscription } from './hooks/useLiveChatMessageEventSubscription';
import { useHandleKustomerChatInitialization } from './hooks/useHandleKustomerChatInitialization';
import { FORETHOUGHT_IFRAME_ID } from 'embed/constants';
import { useGetPersistedLiveChatComponents } from './hooks/useGetPersistedLiveChatComponents';
import { useResolveLiveChatConversation } from './hooks/useResolveLiveChatConversation';
import { AnimatePresence } from 'motion/react';
import { postIframeResizeEvent } from 'utils/postIframeResizeEvent';
import { useHandleSuncoInitialization } from './hooks/useHandleSuncoInitialization';
import { toggleModalVisibility } from 'actions/workflows-v2/workflowsV2';
import { useHandleSalesforceChatInitialization } from './hooks/useHandleSalesforceChatInitialization';

const App: React.FC<React.PropsWithChildren<unknown>> = () => {
  // Effects
  useHandleOnWidgetLoad(); // Needs to be before other init hooks to receive their messages
  useInitSolve(); // Load from iframe
  useInitSolveFromURL(); // Load from URL (mobile)
  useLogRocket();
  useSetupSentry();
  useSetCustomConfigs();
  useInitApiService();
  useSetLastActiveTabId();
  useClearConversationOnTimeout();
  useHandleAttemptedToFetchRedactedConversationError();
  useEmitConversationIdGeneratedEvent();

  // Listeners
  usePostMessageListener();

  // ONE CHATS
  usePusherLiveChatMessageEventSubscription();
  useHandleZendeskChatInitialization();
  useHandleSalesforceChatInitialization();
  useHandleSuncoInitialization();
  useHandleKustomerChatInitialization();
  useGetPersistedLiveChatComponents();
  useResolveLiveChatConversation();

  useSetTabIdSentryTag();

  const dispatch = useAppDispatch();
  const history = useAppSelector(selectHistory);
  const isMobile = useAppSelector(selectIsMobile);
  const isWidgetVisible: boolean = useAppSelector(selectIsWidgetVisible);
  const isHelpButtonVisible = useAppSelector(selectIsHelpButtonVisible);
  const themeColor = useAppSelector(selectThemeColor);
  const currentPage = useAppSelector(selectCurrentPage);
  const tabName = useAppSelector(selectTabName);
  const workflowsOrgConfig = useAppSelector(selectWorkflowsConfig);
  const isBuilderPreview = useAppSelector(selectIsBuilderPreview);
  const isProactiveChatVisible = useAppSelector(selectIsProactiveChatVisible);
  const intentTitle = useAppSelector(selectIntentTitle);
  const isModelTraining = useAppSelector(selectIsModelTraining);
  const isWidgetEnabled = useAppSelector(selectIsWidgetEnabled);
  const initialIntentId = useAppSelector(selectInitialIntentId);
  const initialQuery = useAppSelector(selectInitialQuery);
  const isPositionLeft = useAppSelector(selectIsPositionLeft);
  const positionY = useAppSelector(selectPositionY);
  const featureFlags = useAppSelector(selectFeatureFlags);
  const launchStyle = useAppSelector(selectLaunchStyle);
  const placement = useAppSelector(selectPlacement);
  const handleWorkflowTriggerOnClick = useHandleWorkflowTriggerOnClick();

  const CurrentPageComponent = pageDict[currentPage]?.component;
  const previousPage = history[history.length - 2] ?? ''; // Get the previous page
  const previousPageHeader = pageDict[previousPage]?.headerName;
  const isButtonReady = workflowsOrgConfig?.colors && isHelpButtonVisible;

  const showHelpButton = isButtonReady && !isBuilderPreview;

  const isSolveUiMultipleTabsIframeSizeFixEnabled = featureFlags.includes(
    'solve_ui_multiple_tabs_iframe_size_fix',
  );

  const handleOnClick = () => {
    if (!!initialIntentId || !!initialQuery) {
      handleWorkflowTriggerOnClick();
    } else {
      dispatch(toggleModalVisibility(true));
    }

    dispatch(
      sendTrackingEvent({
        event_type: 'widget-opened',
        mechanism: 'manual-click-on-widget-icon',
      }),
    );
  };

  //Alert WFB that widget has been loaded
  useEffect(() => {
    const workflowConfigExists = Object.values(workflowsOrgConfig || {}).length;
    if (isBuilderPreview && workflowConfigExists)
      postMessage({ event: 'solveWidgetLoaded' });
  }, [isBuilderPreview, workflowsOrgConfig]);

  useEffect(() => {
    /**
     * setup eventListener on pages where the widget is not in an
     * iframe like solve-ui-stage.forethought.tech/?data-api-key=<>,
     * solve-widget.forethought.ai/?data-api-key=<>, and the mobile SDK's
     *
     * ! remove if check if testing locally at localhost:7890/?data-api-key=<>
     * ! because the iframe does render there as well
     */
    if (!window.document.getElementById(FORETHOUGHT_IFRAME_ID)) {
      window.addEventListener('resize', () => {
        postMessage({
          event: 'forethoughtWidgetScreenSizeEvent',
          isMobile: mobileWidthCondition.matches,
          isSmallScreen: smallHeightCondition.matches,
        });
      });
    }
  }, []);

  const isInitializingRef = useRef(true);

  useEffect(() => {
    if (isSolveUiMultipleTabsIframeSizeFixEnabled) {
      if (isWidgetEnabled) {
        if (isInitializingRef.current) {
          isInitializingRef.current = false;

          // Prevent firing `forethoughtWidgetClosed` event when initializing widget:
          if (!isWidgetVisible) {
            return;
          }
        }

        sendResizeiframeMessage(isWidgetVisible);
      }
    } else {
      if (isWidgetEnabled && isWidgetVisible) {
        sendResizeiframeMessage(isWidgetVisible);
      }
    }
  }, [
    isSolveUiMultipleTabsIframeSizeFixEnabled,
    isWidgetEnabled,
    isWidgetVisible,
  ]);

  useEscapeKeyHandler();
  useAnnounceMessageReceived();

  const modalAnimate = useMemo(
    () => ({
      opacity: 1,
      transform: 'scale(1)',
    }),
    [],
  );
  const modalExit = useMemo(
    () => ({
      opacity: 0,
      transform: 'scale(0.9)',
      transition: {
        duration: 0.1,
      },
    }),
    [],
  );
  const modalInitial = useMemo(() => {
    const transformOrigin = (() => {
      if (launchStyle === 'floating_bar') {
        switch (placement) {
          case 'top':
            return 'top center';
          case 'middle':
            return 'center center';
          case 'bottom':
          default:
            return 'bottom center';
        }
      }

      return `${positionY === 'top' ? 'top' : 'bottom'} ${isPositionLeft ? 'left' : 'right'}`;
    })();

    return {
      opacity: 0,
      transform: 'scale(0.5)',
      transformOrigin: transformOrigin,
    };
  }, [isPositionLeft, launchStyle, placement, positionY]);

  const modalTransition = useMemo(
    () => ({
      damping: 16,
      mass: 0.4,
      stiffness: 300,
      type: 'spring',
    }),
    [],
  );

  const helpButtonAnimate = useMemo(
    () => ({
      opacity: 1,
    }),
    [],
  );
  const helpButtonInitial = useMemo(
    () => ({
      opacity: 0,
    }),
    [],
  );
  const helpButtonOnAnimationComplete = useCallback(() => {
    if (!isProactiveChatVisible) {
      postIframeResizeEvent();
    }
  }, [isProactiveChatVisible]);
  const helpButtonTransition = useMemo(
    () => ({
      duration: 0.1,
    }),
    [],
  );

  // Org config failed
  if (workflowsOrgConfig && !workflowsOrgConfig?.colors) return null;

  if (!isWidgetEnabled) {
    return null;
  }

  return (
    <Unstable_TrapFocus disableEnforceFocus open={isWidgetVisible}>
      <Container>
        <AnimatePresence>
          {isWidgetVisible && (
            <ModalContainer
              isBuilderPreview={isBuilderPreview}
              isFloatingBar={launchStyle === 'floating_bar'}
              isMobile={isMobile}
              isPositionLeft={isPositionLeft}
              isWidgetVisible={isWidgetVisible}
              key='Modal'
              placement={placement}
            >
              <Modal
                animate={modalAnimate}
                exit={modalExit}
                headerText={previousPageHeader || tabName}
                initial={modalInitial}
                transition={modalTransition}
              >
                <AppWrapper focusBorderColor={themeColor} id='app-wrapper'>
                  {isBuilderPreview && history.length <= 1 && (
                    <ModelTrainingIndicator
                      intentTitle={intentTitle}
                      isModelTraining={isModelTraining}
                    />
                  )}
                  {CurrentPageComponent && <CurrentPageComponent />}
                </AppWrapper>
              </Modal>
            </ModalContainer>
          )}
          {showHelpButton && (
            <ModalContainer
              isBuilderPreview={isBuilderPreview}
              isFloatingBar={launchStyle === 'floating_bar'}
              isMobile={isMobile}
              isPositionLeft={isPositionLeft}
              isWidgetVisible={isWidgetVisible}
              key='modal-container'
              placement={placement}
            >
              <HelpButtonContainer
                isFloatingBar={launchStyle === 'floating_bar'}
                isPositionLeft={isPositionLeft}
                key='help-button-container'
                placement={placement}
              >
                {/* floating_bar renders the ProactiveBubbles in FloatingBar.tsx */}
                {isProactiveChatVisible && launchStyle !== 'floating_bar' && (
                  <ProactiveBubbles key='proactive-bubbles' />
                )}
                <HelpButton
                  animate={helpButtonAnimate}
                  initial={helpButtonInitial}
                  key='help-button'
                  onAnimationComplete={helpButtonOnAnimationComplete}
                  onClickHandler={handleOnClick}
                  transition={helpButtonTransition}
                />
              </HelpButtonContainer>
            </ModalContainer>
          )}
        </AnimatePresence>
      </Container>
    </Unstable_TrapFocus>
  );
};

const AppWithProfiler = withProfiler(App);

export default AppWithProfiler;
